import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import jsQR from 'jsqr';
import { Field } from '../Field.js';
import { preparerSon } from './retourScan.js';
import styles from './InventairePage.module.css';

export type DecoderQr = (image: ImageData) => string | null | Promise<string | null>;
export type DecoderQrMultiple = (image: ImageData) => string[] | Promise<string[]>;
type Detecteur = { detect: (image: ImageData) => Promise<{ rawValue: string }[]> };
const DetecteurNatif = (
  globalThis as typeof globalThis & {
    BarcodeDetector?: new (options: { formats: string[] }) => Detecteur;
  }
).BarcodeDetector;
function creerDetecteur(): Detecteur | null {
  if (!DetecteurNatif) return null;
  try {
    return new DetecteurNatif({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}
const detecteur = creerDetecteur();
export const decoderQrLocal: DecoderQr = async (image) => {
  if (detecteur) {
    try {
      return (await detecteur.detect(image))[0]?.rawValue ?? null;
    } catch {
      /* Certains navigateurs exposent un détecteur sans prise en charge du QR. */
    }
  }
  return jsQR(image.data, image.width, image.height)?.data ?? null;
};

/** Taille des fenêtres de repli, en part de la hauteur de l'image ; le pas est la moitié de la fenêtre. */
const FENETRES_JSQR = [0.5, 0.33];
/** Côté maximal lu par le repli jsQR : au-delà, l'image est réduite, le coût croît avec les pixels. */
const COTE_MAX_JSQR = 960;
const DELAI_REESSAI_MS = 2000;
let imagesRepli = 0;
/**
 * Tous les QR d'une image. `BarcodeDetector` les rend d'un coup. Sans lui, jsQR ne sait lire qu'un
 * code isolé : on lit l'image entière, puis des fenêtres qui se recouvrent, en effaçant chaque code
 * trouvé pour ne pas le relire. C'est le scan successif rapide, au prix de plus de calcul.
 */
export const decoderQrMultipleLocal: DecoderQrMultiple = async (image) => {
  if (detecteur) {
    try {
      return (await detecteur.detect(image)).map((code) => code.rawValue).filter(Boolean);
    } catch {
      /* Même repli que `decoderQrLocal`. */
    }
  }
  const reduite = reduire(image, COTE_MAX_JSQR);
  image = reduite;
  const pixels = new Uint8ClampedArray(image.data);
  const trouves: string[] = [];
  function lire(x: number, y: number, largeur: number, hauteur: number): void {
    const fenetre = extraire(pixels, image.width, x, y, largeur, hauteur);
    const code = jsQR(fenetre, largeur, hauteur);
    if (!code) return;
    if (!trouves.includes(code.data)) trouves.push(code.data);
    const coins = [
      code.location.topLeftCorner,
      code.location.topRightCorner,
      code.location.bottomLeftCorner,
      code.location.bottomRightCorner,
    ];
    effacer(
      pixels,
      image,
      x + Math.min(...coins.map((c) => c.x)),
      y + Math.min(...coins.map((c) => c.y)),
      x + Math.max(...coins.map((c) => c.x)),
      y + Math.max(...coins.map((c) => c.y)),
    );
  }
  lire(0, 0, image.width, image.height);
  // Les fenêtres coûtent plus que l'image entière : un code qui reste en vue est pris à l'image suivante.
  if (imagesRepli++ % 2 !== 0) return trouves;
  for (const part of FENETRES_JSQR) {
    const cote = Math.min(Math.round(image.height * part), image.width);
    const pas = Math.max(1, Math.round(cote / 2));
    for (let y = 0; y + cote <= image.height + pas - 1; y += pas) {
      for (let x = 0; x + cote <= image.width + pas - 1; x += pas) {
        lire(
          Math.min(x, image.width - cote),
          Math.min(y, image.height - cote),
          cote,
          Math.min(cote, image.height),
        );
      }
    }
  }
  return trouves;
};
/** Réduit par échantillonnage (plus proche voisin) : le QR garde assez de modules pour être lu. */
function reduire(image: ImageData, coteMax: number): ImageData {
  const grand = Math.max(image.width, image.height);
  if (grand <= coteMax) return image;
  const k = coteMax / grand;
  const largeur = Math.max(1, Math.round(image.width * k));
  const hauteur = Math.max(1, Math.round(image.height * k));
  const data = new Uint8ClampedArray(largeur * hauteur * 4);
  for (let y = 0; y < hauteur; y++) {
    const source = Math.min(image.height - 1, Math.floor(y / k)) * image.width;
    for (let x = 0; x < largeur; x++) {
      const i = (source + Math.min(image.width - 1, Math.floor(x / k))) * 4;
      data.set(image.data.subarray(i, i + 4), (y * largeur + x) * 4);
    }
  }
  return { data, width: largeur, height: hauteur, colorSpace: image.colorSpace } as ImageData;
}
function extraire(
  pixels: Uint8ClampedArray,
  largeurImage: number,
  x: number,
  y: number,
  largeur: number,
  hauteur: number,
): Uint8ClampedArray {
  const fenetre = new Uint8ClampedArray(largeur * hauteur * 4);
  for (let ligne = 0; ligne < hauteur; ligne++) {
    const debut = ((y + ligne) * largeurImage + x) * 4;
    fenetre.set(pixels.subarray(debut, debut + largeur * 4), ligne * largeur * 4);
  }
  return fenetre;
}
function effacer(
  pixels: Uint8ClampedArray,
  image: { width: number; height: number },
  gauche: number,
  haut: number,
  droite: number,
  bas: number,
): void {
  const x0 = Math.max(0, Math.floor(gauche) - 4);
  const x1 = Math.min(image.width - 1, Math.ceil(droite) + 4);
  const y0 = Math.max(0, Math.floor(haut) - 4);
  const y1 = Math.min(image.height - 1, Math.ceil(bas) + 4);
  for (let y = y0; y <= y1; y++) {
    pixels.fill(255, (y * image.width + x0) * 4, (y * image.width + x1 + 1) * 4);
  }
}

export function Scanner({
  onScan,
  decoderQr = decoderQrLocal,
  decoderQrMultiple = decoderQrMultipleLocal,
  multiple = false,
  desactive = false,
}: {
  onScan: (code: string) => Promise<void> | void;
  decoderQr?: DecoderQr;
  /** Mode panier : chaque QR visible est remis à `onScan`, chacun une fois tant qu'il reste en vue. */
  decoderQrMultiple?: DecoderQrMultiple;
  multiple?: boolean;
  desactive?: boolean;
}) {
  const { t } = useTranslation('regie');
  const id = useId();
  const video = useRef<HTMLVideoElement>(null);
  const rappel = useRef(onScan);
  const occupe = useRef(false);
  const [camera, setCamera] = useState(false);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    rappel.current = onScan;
  }, [onScan]);
  useEffect(() => {
    if (!camera || desactive) return;
    let fini = false;
    let flux: MediaStream | undefined;
    let minuterie: ReturnType<typeof setTimeout> | undefined;
    let precedent: string | null = null;
    let enVue = new Set<string>();
    const reessai = new Map<string, number>();
    const toile = document.createElement('canvas');
    async function lire() {
      const element = video.current;
      if (fini || !element) return;
      if (element.readyState >= 2 && !occupe.current) {
        toile.width = element.videoWidth;
        toile.height = element.videoHeight;
        const contexte = toile.getContext('2d', { willReadFrequently: true });
        if (contexte && toile.width && toile.height) {
          contexte.drawImage(element, 0, 0);
          try {
            const image = contexte.getImageData(0, 0, toile.width, toile.height);
            if (multiple) {
              const codes = [...new Set(await decoderQrMultiple(image))];
              if (fini) return;
              const maintenant = Date.now();
              const bloque = (code: string) => (reessai.get(code) ?? 0) > maintenant;
              const nouveaux = codes.filter((code) => !enVue.has(code) && !bloque(code));
              enVue = new Set(codes.filter((code) => !bloque(code)));
              occupe.current = true;
              let recus = 0;
              for (const code of nouveaux) {
                if (fini) return;
                try {
                  await rappel.current(code);
                  recus++;
                } catch {
                  // Un échec passager : le code, toujours en vue, repart après un court répit.
                  enVue.delete(code);
                  reessai.set(code, Date.now() + DELAI_REESSAI_MS);
                }
              }
              if (recus) setMessage(t('inventaire.scanRecu'));
            } else {
              const trouve = await decoderQr(image);
              if (fini) return;
              if (trouve && trouve !== precedent) {
                precedent = trouve;
                occupe.current = true;
                await rappel.current(trouve);
                setMessage(t('inventaire.scanRecu'));
              }
              if (!trouve) precedent = null;
            }
          } catch {
            if (!fini) setMessage(t('inventaire.erreurScan'));
          } finally {
            occupe.current = false;
          }
        }
      }
      if (!fini) minuterie = setTimeout(() => void lire(), 250);
    }
    void (async () => {
      try {
        flux = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (fini) {
          flux.getTracks().forEach((piste) => piste.stop());
          return;
        }
        if (video.current) {
          video.current.srcObject = flux;
          await video.current.play();
        }
        await lire();
      } catch {
        if (!fini) setMessage(t('inventaire.cameraIndisponible'));
      }
    })();
    return () => {
      fini = true;
      clearTimeout(minuterie);
      flux?.getTracks().forEach((piste) => piste.stop());
    };
  }, [camera, decoderQr, decoderQrMultiple, desactive, multiple, t]);
  async function soumettre() {
    if (!code.trim() || occupe.current || desactive) return;
    occupe.current = true;
    setEnCours(true);
    try {
      await onScan(code.trim());
      setCode('');
      setMessage(t('inventaire.scanRecu'));
    } catch {
      setMessage(t('inventaire.erreurScan'));
    } finally {
      occupe.current = false;
      setEnCours(false);
    }
  }
  return (
    <div className={styles.scanner}>
      <Button
        variant="secondary"
        disabled={desactive}
        onClick={() => {
          // Safari n'ouvre le son que depuis un geste : on le prépare au clic.
          if (!camera && multiple) preparerSon();
          setCamera(!camera);
        }}
      >
        {t(camera ? 'inventaire.arreterCamera' : 'inventaire.camera')}
      </Button>
      {camera && (
        <video
          ref={video}
          muted
          playsInline
          className={styles.video}
          aria-label={t('inventaire.camera')}
        />
      )}
      <form
        className={styles.formulaire}
        data-lecture-seule="ok"
        onSubmit={(e) => {
          e.preventDefault();
          void soumettre();
        }}
      >
        <Field htmlFor={id} label={t('inventaire.code')}>
          <Input
            id={id}
            value={code}
            maxLength={200}
            disabled={desactive}
            autoComplete="off"
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <Button type="submit" disabled={desactive || !code.trim() || enCours}>
          {t('inventaire.lire')}
        </Button>
      </form>
      <p role="status">{message}</p>
    </div>
  );
}
